import React, { useState } from 'react';
import { ShippingBanner } from '../components/ShippingBanner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { Mail, Instagram, Clock, MessageCircle, Send, ChevronDown, ChevronUp, MapPin, Sparkles } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string;
}
const faqs: FAQItem[] = [{
  question: 'What payment methods do you accept?',
  answer: "We currently accept Zelle and Cash App for all orders. After placing your order, you'll receive detailed payment instructions via email."
}, {
  question: 'How long does shipping take?',
  answer: "Orders are processed within 1-2 business days. Standard shipping typically takes 3-5 business days within the continental U.S. You'll receive tracking information once your order ships."
}, {
  question: 'What is your return policy?',
  answer: 'We accept returns of unworn items with original tags within 30 days of delivery. Please contact us to initiate a return and receive your prepaid shipping label.'
}, {
  question: 'Do you offer international shipping?',
  answer: "Currently, we only ship within the United States. We're working on expanding our shipping options—follow us on Instagram for updates!"
}, {
  question: 'How do I know my size?',
  answer: "Each product page includes a detailed size guide. If you're between sizes or unsure, feel free to reach out—we're happy to help you find the perfect fit."
}];
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };
  const inputStyles = `w-full px-3 sm:px-4 py-3 bg-white border border-[#E8E8E8] text-[#1A1A1A] text-base font-['Raleway'] focus:outline-none focus:border-[#D4A853] focus:ring-1 focus:ring-[#D4A853]/20 transition-all duration-200 placeholder-[#6B6B6B]`;
  const labelStyles = 'block text-sm font-medium text-[#6B6B6B] uppercase tracking-[0.12em] mb-2';
  return <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-id="element-782">
      <ShippingBanner data-id="element-783" />
      <Navbar data-id="element-784" />

      <main className="flex-grow" data-id="element-785">
        {/* Hero Section */}
        <section className="relative bg-[#1A1A1A] text-white overflow-hidden" data-id="element-786">
          <div className="absolute inset-0" data-id="element-787">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-[#D4A853]/10 transform skew-x-12 translate-x-32" data-id="element-788" />
            <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-[#D4A853]/5 rounded-tr-[100px]" data-id="element-789" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20" data-id="element-790">
            <div className="max-w-2xl" data-id="element-791">
              <div className="flex items-center gap-2 mb-4" data-id="element-792">
                <Sparkles size={16} className="text-[#D4A853]" data-id="element-793" />
                <span className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#D4A853] font-['Raleway']" data-id="element-794">
                  Get in Touch
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-['Playfair_Display'] leading-[1.1] mb-4 sm:mb-6" data-id="element-795">
                We'd Love to <span className="italic text-[#D4A853]" data-id="element-796">Hear</span>{' '}
                From You
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/70 font-['Raleway'] leading-relaxed max-w-lg" data-id="element-797">
                Have a question about an order, need styling advice, or just
                want to say hello? We're here to help.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 sm:py-16 lg:py-20" data-id="element-798">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-799">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-800">
              {/* Contact Form */}
              <div className="lg:col-span-7" data-id="element-801">
                <div className="bg-white p-6 sm:p-8 border border-[#E8E8E8]" data-id="element-802">
                  <div className="flex items-center gap-3 mb-6" data-id="element-803">
                    <div className="w-12 h-12 rounded-full bg-[#D4A853]/10 flex items-center justify-center" data-id="element-804">
                      <MessageCircle size={22} className="text-[#D4A853]" strokeWidth={1.5} data-id="element-805" />
                    </div>
                    <div data-id="element-806">
                      <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] text-[#1A1A1A]" data-id="element-807">
                        Send a Message
                      </h2>
                      <p className="text-sm sm:text-base text-[#6B6B6B] font-['Raleway']" data-id="element-808">
                        We typically respond within 24 hours
                      </p>
                    </div>
                  </div>

                  {isSubmitted ? <div className="text-center py-12" data-id="element-809">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#D4A853]/10 flex items-center justify-center" data-id="element-810">
                        <Send size={28} className="text-[#D4A853]" strokeWidth={1.5} data-id="element-811" />
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-['Playfair_Display'] text-[#1A1A1A] mb-2" data-id="element-812">
                        Message Sent!
                      </h3>
                      <p className="text-base sm:text-lg text-[#6B6B6B] font-['Raleway'] mb-6" data-id="element-813">
                        Thank you for reaching out. We'll get back to you soon.
                      </p>
                      <Button variant="outline" onClick={() => setIsSubmitted(false)} className="text-base" data-id="element-814">
                        Send Another Message
                      </Button>
                    </div> : <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-id="element-815">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-816">
                        <div data-id="element-817">
                          <label htmlFor="name" className={labelStyles} data-id="element-818">
                            Name
                          </label>
                          <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your name" required className={inputStyles} data-id="element-819" />
                        </div>
                        <div data-id="element-820">
                          <label htmlFor="email" className={labelStyles} data-id="element-821">
                            Email
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" required className={inputStyles} data-id="element-822" />
                        </div>
                      </div>
                      <div data-id="element-823">
                        <label htmlFor="subject" className={labelStyles} data-id="element-824">
                          Subject
                        </label>
                        <select id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required className={`${inputStyles} cursor-pointer`} data-id="element-825">
                          <option value="" data-id="element-826">Select a topic</option>
                          <option value="order" data-id="element-827">Order Inquiry</option>
                          <option value="sizing" data-id="element-828">Sizing Help</option>
                          <option value="returns" data-id="element-829">Returns & Exchanges</option>
                          <option value="styling" data-id="element-830">Styling Advice</option>
                          <option value="wholesale" data-id="element-831">Wholesale Inquiry</option>
                          <option value="other" data-id="element-832">Other</option>
                        </select>
                      </div>
                      <div data-id="element-833">
                        <label htmlFor="message" className={labelStyles} data-id="element-834">
                          Message
                        </label>
                        <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="How can we help you?" required rows={5} className={`${inputStyles} resize-none`} data-id="element-835" />
                      </div>
                      <Button type="submit" variant="secondary" className="w-full h-12 sm:h-[52px] text-base" data-id="element-836">
                        <Send size={16} className="mr-2" data-id="element-837" /> Send Message
                      </Button>
                    </form>}
                </div>
              </div>

              {/* Contact Info Sidebar */}
              <div className="lg:col-span-5 space-y-6" data-id="element-838">
                <div className="bg-white p-6 border border-[#E8E8E8]" data-id="element-839">
                  <h3 className="text-xl sm:text-2xl font-['Playfair_Display'] text-[#1A1A1A] mb-5" data-id="element-840">
                    Direct Contact
                  </h3>
                  <div className="space-y-4" data-id="element-841">
                    <a href="mailto:christiana.nnanna@email.com" className="flex items-start gap-3 group" data-id="element-842">
                      <div className="w-11 h-11 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/10 transition-colors" data-id="element-843">
                        <Mail size={18} className="text-[#6B6B6B] group-hover:text-[#D4A853] transition-colors" strokeWidth={1.5} data-id="element-844" />
                      </div>
                      <div data-id="element-845">
                        <p className="text-sm text-[#6B6B6B] font-['Raleway'] uppercase tracking-wider mb-0.5" data-id="element-846">
                          Email
                        </p>
                        <p className="text-base sm:text-lg text-[#1A1A1A] font-['Raleway'] group-hover:text-[#D4A853] transition-colors break-all" data-id="element-847">
                          christiana.nnanna@email.com
                        </p>
                      </div>
                    </a>
                    <a href="https://instagram.com/christiana.nnanna" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group" data-id="element-848">
                      <div className="w-11 h-11 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4A853]/10 transition-colors" data-id="element-849">
                        <Instagram size={18} className="text-[#6B6B6B] group-hover:text-[#D4A853] transition-colors" strokeWidth={1.5} data-id="element-850" />
                      </div>
                      <div data-id="element-851">
                        <p className="text-sm text-[#6B6B6B] font-['Raleway'] uppercase tracking-wider mb-0.5" data-id="element-852">
                          Instagram
                        </p>
                        <p className="text-base sm:text-lg text-[#1A1A1A] font-['Raleway'] group-hover:text-[#D4A853] transition-colors" data-id="element-853">
                          @christiana.nnanna
                        </p>
                      </div>
                    </a>
                    <div className="flex items-start gap-3" data-id="element-854">
                      <div className="w-11 h-11 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0" data-id="element-855">
                        <MapPin size={18} className="text-[#6B6B6B]" strokeWidth={1.5} data-id="element-856" />
                      </div>
                      <div data-id="element-857">
                        <p className="text-sm text-[#6B6B6B] font-['Raleway'] uppercase tracking-wider mb-0.5" data-id="element-858">
                          Location
                        </p>
                        <p className="text-base sm:text-lg text-[#1A1A1A] font-['Raleway']" data-id="element-859">
                          Ships across the United States
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#D4A853]/10 p-5 border-l-2 border-[#D4A853]" data-id="element-860">
                  <div className="flex items-center gap-2 mb-2" data-id="element-861">
                    <Clock size={16} className="text-[#D4A853]" strokeWidth={1.5} data-id="element-862" />
                    <p className="text-sm text-[#6B6B6B] font-['Raleway'] uppercase tracking-wider" data-id="element-863">
                      Response Time
                    </p>
                  </div>
                  <p className="text-base sm:text-lg text-[#1A1A1A] font-['Raleway'] leading-relaxed" data-id="element-864">
                    We typically respond within{' '}
                    <span className="font-medium" data-id="element-865">24 hours</span> during
                    business days. For urgent order inquiries, please include
                    your order number.
                  </p>
                </div>

                <div className="bg-white p-6 border border-[#E8E8E8]" data-id="element-866">
                  <h3 className="text-xl sm:text-2xl font-['Playfair_Display'] text-[#1A1A1A] mb-4" data-id="element-867">
                    Business Hours
                  </h3>
                  <div className="space-y-2 text-base sm:text-lg font-['Raleway']" data-id="element-868">
                    <div className="flex justify-between" data-id="element-869">
                      <span className="text-[#6B6B6B]" data-id="element-870">Monday – Friday</span>
                      <span className="text-[#1A1A1A]" data-id="element-871">
                        9:00 AM – 6:00 PM EST
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-872">
                      <span className="text-[#6B6B6B]" data-id="element-873">Saturday</span>
                      <span className="text-[#1A1A1A]" data-id="element-874">
                        10:00 AM – 4:00 PM EST
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-875">
                      <span className="text-[#6B6B6B]" data-id="element-876">Sunday</span>
                      <span className="text-[#6B6B6B] italic" data-id="element-877">Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white" data-id="element-878">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-879">
            <div className="text-center mb-10 sm:mb-12" data-id="element-880">
              <p className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#6B6B6B] mb-2 sm:mb-3 font-['Raleway']" data-id="element-881">
                Common Questions
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-['Playfair_Display'] text-[#1A1A1A]" data-id="element-882">
                Frequently Asked
              </h2>
              <div className="w-10 sm:w-12 h-[2px] bg-[#D4A853] mx-auto mt-4" data-id="element-883" />
            </div>

            <div className="space-y-3" data-id="element-884">
              {faqs.map((faq, index) => <div key={index} className="border border-[#E8E8E8] bg-[#FAFAFA]" data-id="element-885">
                  <button onClick={() => setOpenFAQ(openFAQ === index ? null : index)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left" data-id="element-886">
                    <span className="text-base sm:text-lg font-medium text-[#1A1A1A] font-['Raleway'] pr-4" data-id="element-887">
                      {faq.question}
                    </span>
                    {openFAQ === index ? <ChevronUp size={20} className="text-[#D4A853] flex-shrink-0" data-id="element-888" /> : <ChevronDown size={20} className="text-[#6B6B6B] flex-shrink-0" data-id="element-889" />}
                  </button>
                  {openFAQ === index && <div className="px-4 sm:px-5 pb-4 sm:pb-5" data-id="element-890">
                      <p className="text-base sm:text-lg text-[#6B6B6B] font-['Raleway'] leading-relaxed" data-id="element-891">
                        {faq.answer}
                      </p>
                    </div>}
                </div>)}
            </div>

            <p className="text-center text-base sm:text-lg text-[#6B6B6B] font-['Raleway'] mt-8" data-id="element-892">
              Still have questions?{' '}
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="text-[#D4A853] hover:text-[#B8923F] transition-colors underline underline-offset-2" data-id="element-893">
                Send us a message
              </button>
            </p>
          </div>
        </section>
      </main>

      <Footer data-id="element-894" />
    </div>;
}